import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil as EditIcon, Trash2 as DeleteIcon, HeartPulse as HealthIcon, FlaskConical as ScienceIcon } from 'lucide-react';
import { Chip } from '@coordio/ui';
import { mcpService, HealthStatus, type McpServerDto, type McpServerAgentAssignmentDto } from '../services/mcpService';
import { useNotificationStore } from '../store/notificationStore';
import { CodeEditor } from '../components/common/CodeEditor';
import { FormSection } from '@coordio/ui/layout';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { useConfirmDialog } from '../components/common/ConfirmDialog';

const healthStatusColor = (status: string): 'green' | 'red' | 'amber' | 'default' => {
  switch (status) {
    case HealthStatus.Healthy:
      return 'green';
    case HealthStatus.Unhealthy:
      return 'red';
    case HealthStatus.Degraded:
      return 'amber';
    default:
      return 'default';
  }
};

/**
 * MCP server detail page with Overview, Tools, and Agents tabs.
 */
export const McpServerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [server, setServer] = useState<McpServerDto | null>(null);
  const [agents, setAgents] = useState<McpServerAgentAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);

  const loadServer = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await mcpService.getMcpServer(id);
      setServer(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load MCP server';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    if (!id) return;
    try {
      const list = await mcpService.getMcpServerAgents(id);
      setAgents(list);
    } catch {
      setAgents([]);
    }
  };

  useEffect(() => {
    loadServer();
  }, [id]);

  useEffect(() => {
    if (id && server) {
      loadAgents();
    }
  }, [id, server?.id]);

  const handleDiscoverTools = async () => {
    if (!id) return;
    setDiscovering(true);
    try {
      const result = await mcpService.discoverTools(id);
      addNotification(`Discovered ${result.toolsDiscovered} tools`, 'success');
      loadServer();
    } catch (err: any) {
      addNotification(err.message || 'Failed to discover tools', 'error');
    } finally {
      setDiscovering(false);
    }
  };

  const handleCheckHealth = async () => {
    if (!id) return;
    setCheckingHealth(true);
    try {
      const result = await mcpService.checkHealth(id);
      if (result.isHealthy) {
        addNotification('Server is healthy', 'success');
      } else {
        addNotification(`Health check failed: ${result.errorMessage || 'Unknown error'}`, 'warning');
      }
      loadServer();
    } catch (err: any) {
      addNotification(err.message || 'Failed to check health', 'error');
    } finally {
      setCheckingHealth(false);
    }
  };

  const handleDelete = async () => {
    if (!server) return;
    const ok = await confirm({
      title: 'Delete MCP server',
      message: `Are you sure you want to delete "${server.name}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await mcpService.deleteMcpServer(server.id);
      addNotification('MCP server deleted successfully', 'success');
      navigate('/mcp-servers');
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to delete MCP server', 'error');
    }
  };

  const parseHeadersJson = (json: string | undefined): Record<string, string> => {
    if (!json || !json.trim()) return {};
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    } catch {
      return {};
    }
  };

  return (
    <PageDetailLayout
      title={server?.name || 'MCP Server'}
      data-testid="mcp-server-form-page"
      breadcrumbs={[{ label: 'MCP Servers', href: '/mcp-servers' }, { label: server?.name || 'MCP Server' }]}
      backTo={{ label: 'MCP Servers', href: '/mcp-servers' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={server ? { label: server.isActive ? 'Active' : 'Inactive', color: server.isActive ? 'green' : 'red' } : undefined}
      chips={server ? [
        { label: server.healthStatus, color: healthStatusColor(server.healthStatus) },
        { label: `${server.toolCount} tools`, variant: 'outlined' as const },
      ] : []}
      actions={server ? [
        {
          icon: discovering
            ? <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            : <ScienceIcon />,
          label: 'Discover Tools',
          onClick: handleDiscoverTools,
          disabled: discovering,
        },
        {
          icon: checkingHealth
            ? <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            : <HealthIcon />,
          label: 'Check Health',
          onClick: handleCheckHealth,
          disabled: checkingHealth,
        },
        { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/mcp-servers/${server.id}/edit`) },
        { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
      ] : []}
      tabs={server ? [
        {
          label: 'Overview',
          value: 'overview',
          content: (
            <div>
              <FormSection title="Description" data-testid="mcp-server-section-description">
                <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                  {server.description || 'No description available'}
                </p>
              </FormSection>

              <FormSection title="Details" data-testid="mcp-server-section-details">
                <dl className="grid grid-cols-[auto_1fr] gap-2">
                  <dt className="font-medium">Server URL:</dt>
                  <dd className="text-[var(--color-text-secondary)] break-all">{server.serverUrl}</dd>
                  <dt className="font-medium">Health status:</dt>
                  <dd>
                    <Chip color={healthStatusColor(server.healthStatus)} label={server.healthStatus} />
                  </dd>
                  <dt className="font-medium">Last health check:</dt>
                  <dd className="text-[var(--color-text-secondary)]">
                    {server.lastHealthCheckAt ? new Date(server.lastHealthCheckAt).toLocaleString() : 'Never'}
                  </dd>
                  <dt className="font-medium">Tools discovered:</dt>
                  <dd className="text-[var(--color-text-secondary)]">{server.toolCount}</dd>
                </dl>
              </FormSection>

              {server.headersJson && (() => {
                const headers = parseHeadersJson(server.headersJson);
                const entries = Object.entries(headers);
                if (entries.length === 0) return null;
                return (
                  <FormSection title="Custom Headers" data-testid="mcp-server-section-custom-headers">
                    <CodeEditor value={JSON.stringify(headers, null, 2)} language="json" height="120px" readOnly />
                  </FormSection>
                );
              })()}
            </div>
          ),
        },
        {
          label: 'Tools',
          value: 'tools',
          content: (
            <FormSection title={`Discovered Tools (${server.tools?.length ?? server.toolCount ?? 0})`} data-testid="mcp-server-section-tools">
              {server.tools && server.tools.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border-default)]">
                      <th className="py-2 px-3 text-left font-medium text-[var(--color-text-secondary)]">Tool name</th>
                      <th className="py-2 px-3 text-left font-medium text-[var(--color-text-secondary)]">Description</th>
                      <th className="py-2 px-3 text-left font-medium text-[var(--color-text-secondary)]">Discovered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {server.tools.map((t) => (
                      <tr key={t.id} className="border-b border-[var(--color-border-default)]">
                        <td className="py-2 px-3">{t.toolName}</td>
                        <td className="py-2 px-3">{t.description || '—'}</td>
                        <td className="py-2 px-3">{t.discoveredAt ? new Date(t.discoveredAt).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No tools discovered yet. Use &quot;Discover Tools&quot; to fetch tools from the server.
                </p>
              )}
            </FormSection>
          ),
        },
        {
          label: 'Agents',
          value: 'agents',
          content: (
            <FormSection title="Agents using this server" data-testid="mcp-server-section-agents">
              {agents.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border-default)]">
                      <th className="py-2 px-3 text-left font-medium text-[var(--color-text-secondary)]">Agent</th>
                      <th className="py-2 px-3 text-left font-medium text-[var(--color-text-secondary)]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a) => (
                      <tr key={a.id} className="border-b border-[var(--color-border-default)]">
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            className="text-sm text-[var(--color-text-link)] hover:underline"
                            onClick={() => navigate(`/agents/${a.agentDefinitionId}`)}
                          >
                            {a.agentName}
                          </button>
                        </td>
                        <td className="py-2 px-3">
                          <Chip color={a.isEnabled ? 'green' : 'default'} label={a.isEnabled ? 'Enabled' : 'Disabled'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No agents are assigned to this MCP server. Assign from an agent&apos;s MCP Servers tab.
                </p>
              )}
            </FormSection>
          ),
        },
      ] : []}
    />
  );
};
